import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import path from 'path';
import slash from 'slash';

interface CameraData {
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  path: string;
  name: string;
}

interface PointCloudData {
  path: string;
}

export interface Asset {
  name: string;
  id: string;
  date: string;
  visible: boolean;
  folderID: string;
  type: AssetType;
  data: PointCloudData | CameraData[];
}

export enum AssetType {
  LAS,
  Encompass,
  Panoramic,
  Unknown
}

export const assetTypeFromExtension = (extension: string): AssetType => {
  if (extension === '.las') {
    return AssetType.LAS;
  }

  if (extension === '.idx') {
    return AssetType.Encompass;
  }

  if ((extension === '.csv') || (extension === ".txt")) {
    return AssetType.Panoramic;
  }

  return AssetType.Unknown;
};

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: [] as Asset[],
  reducers: {
    addAsset: {
      reducer: (state, action: PayloadAction<Asset>) => {
        state.push(action.payload);
      },
      prepare: (folderID: string, assetPath: string, data?: CameraData[]): {payload: Asset} => {
        assetPath = slash(assetPath);
        const extension = path.extname(assetPath);

        return {
          payload: {
            name: path.basename(assetPath),
            id: nanoid(),
            date: new Date().toISOString(),
            type: assetTypeFromExtension(extension),
            folderID,
            visible: true,
            data: data ? data : {path: extension === '.idx' ? path.dirname(assetPath) : assetPath}
          }
        };
      }
    },
    changeFolder: (state, action: PayloadAction<{folderID: string, assetID: string}>) => {
      const {assetID, folderID} = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.folderID = folderID;
      }
    },
    changeName: (state, action) => {
      const {assetID, name} = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.name = name;
      }
    },
    toggleVisibility: (state, action) => {
      const assetID = action.payload;
      const asset = state.find(asset => asset.id === assetID);

      if (asset) {
        asset.visible = !asset.visible;
      }
    },
    deleteAsset: (state, action) =>
      state.filter(asset => asset.id !== action.payload),
    deleteAssets: (state, action) =>
      state.filter(asset => asset.folderID !== action.payload)
  }
});

export default assetsSlice.reducer;

export const {
  addAsset, changeFolder, toggleVisibility, changeName,
  deleteAsset, deleteAssets
} = assetsSlice.actions;

export const selectAllAssets = state =>
  state.assets;

export const selectPointClouds = state =>
  state.assets.filter(asset => asset.type === AssetType.Encompass || asset.type === AssetType.LAS);

export const selectPanoramics = state =>
  state.assets.filter(asset => asset.type === AssetType.Panoramic);

export const selectAssetsByFolderID = folderID => state =>
  state.assets.filter(asset => asset.folderID === folderID);

export const selectAssetByID = assetID => state =>
  state.assets.find(asset => asset.id === assetID);